import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
// ...existing code...
import {
  Add,
  Edit,
  Delete,
  Search,
  CameraAlt,
  Videocam,
  FlightTakeoff,
  MusicNote,
  EventNote,
  Inventory2,
  AttachMoney,
  Description,
  Category,
  LocalOffer,
} from "@mui/icons-material";
import {
  servicioService,
  paqueteService,
} from "../../services/servicioService";
import MobileDataCard, {
  MobileDataList,
} from "../../components/common/MobileDataCard";

// Iconos predefinidos para servicios basados en A la Vuelta 360°
const SERVICIO_ICONS = {
  fotografía: <CameraAlt />,
  fotografia: <CameraAlt />,
  foto: <CameraAlt />,
  video: <Videocam />,
  videografía: <Videocam />,
  drone: <FlightTakeoff />,
  dron: <FlightTakeoff />,
  aéreo: <FlightTakeoff />,
  dj: <MusicNote />,
  música: <MusicNote />,
  audio: <MusicNote />,
  coordinación: <EventNote />,
  coordinacion: <EventNote />,
  planeación: <EventNote />,
  default: <Category />,
};

const getServiceIcon = (nombre) => {
  if (!nombre) return SERVICIO_ICONS.default;
  const lowerName = nombre.toLowerCase();
  for (const [key, icon] of Object.entries(SERVICIO_ICONS)) {
    if (lowerName.includes(key)) return icon;
  }
  return SERVICIO_ICONS.default;
};

// Colores para paquetes según precio
const getPaqueteColor = (precio) => {
  const p = parseFloat(precio) || 0;
  if (p >= 50000) return { bg: "#fef3c7", color: "#92400e", label: "Premium" };
  if (p >= 25000) return { bg: "#dbeafe", color: "#1e40af", label: "Pro" };
  if (p >= 10000) return { bg: "#d1fae5", color: "#065f46", label: "Estándar" };
  return { bg: "#f3f4f6", color: "#374151", label: "Básico" };
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const AdminServicios = () => {
  // Detección de móvil
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Tab State
  const [tabValue, setTabValue] = useState(0);

  // Servicios State
  const [servicios, setServicios] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(true);
  const [errorServicios, setErrorServicios] = useState("");

  // Paquetes State
  const [paquetes, setPaquetes] = useState([]);
  const [loadingPaquetes, setLoadingPaquetes] = useState(true);
  const [errorPaquetes, setErrorPaquetes] = useState("");

  // Common State
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [openServicio, setOpenServicio] = useState(false);
  const [openPaquete, setOpenPaquete] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // Form State
  const initialServicio = { nombre: "", descripcion: "", precio: "" };
  const initialPaquete = { nombre: "", descripcion: "", precio: "" };
  const [servicioForm, setServicioForm] = useState(initialServicio);
  const [paqueteForm, setPaqueteForm] = useState(initialPaquete);

  // Fetch Servicios
  const fetchServicios = async () => {
    try {
      setLoadingServicios(true);
      const list = await servicioService.getAll();
      setServicios(Array.isArray(list) ? list : []);
      setErrorServicios("");
    } catch (err) {
      console.error("[AdminServicios] Error cargando servicios:", err);
      setErrorServicios(
        err.response?.data?.message || "Error al cargar servicios",
      );
      setServicios([]);
    } finally {
      setLoadingServicios(false);
    }
  };

  // Fetch Paquetes
  const fetchPaquetes = async () => {
    try {
      setLoadingPaquetes(true);
      const list = await paqueteService.getAll();
      setPaquetes(Array.isArray(list) ? list : []);
      setErrorPaquetes("");
    } catch (err) {
      console.error("[AdminServicios] Error cargando paquetes:", err);
      setErrorPaquetes(
        err.response?.data?.message || "Error al cargar paquetes",
      );
      setPaquetes([]);
    } finally {
      setLoadingPaquetes(false);
    }
  };

  useEffect(() => {
    fetchServicios();
    fetchPaquetes();
  }, []);

  // Change detection
  const hasServicioChanges = useMemo(() => {
    if (!isEdit) return true;
    if (!originalData) return false;
    const normalize = (val) =>
      val === null || val === undefined ? "" : String(val).trim();
    return ["nombre", "descripcion", "precio"].some(
      (f) => normalize(servicioForm[f]) !== normalize(originalData[f]),
    );
  }, [isEdit, originalData, servicioForm]);

  const hasPaqueteChanges = useMemo(() => {
    if (!isEdit) return true;
    if (!originalData) return false;
    const normalize = (val) =>
      val === null || val === undefined ? "" : String(val).trim();
    return ["nombre", "descripcion", "precio"].some(
      (f) => normalize(paqueteForm[f]) !== normalize(originalData[f]),
    );
  }, [isEdit, originalData, paqueteForm]);

  // Handlers Servicio
  const handleOpenServicio = (servicio = null) => {
    if (servicio) {
      setIsEdit(true);
      setCurrentId(servicio.id);
      const data = {
        nombre: servicio.nombre || "",
        descripcion: servicio.descripcion || "",
        precio: servicio.precio || "",
      };
      setServicioForm({ ...data });
      setOriginalData({ ...data });
    } else {
      setIsEdit(false);
      setCurrentId(null);
      setServicioForm(initialServicio);
      setOriginalData(null);
    }
    setOpenServicio(true);
  };

  const handleCloseServicio = () => {
    setOpenServicio(false);
    setServicioForm(initialServicio);
    setOriginalData(null);
  };

  const handleSubmitServicio = async (e) => {
    e.preventDefault();
    if (!servicioForm.nombre.trim()) {
      alert("El nombre del servicio es requerido");
      return;
    }
    try {
      if (isEdit) {
        await servicioService.update({ ...servicioForm, id: currentId });
        alert("✓ Servicio actualizado correctamente");
      } else {
        await servicioService.create(servicioForm);
        alert("✓ Servicio creado exitosamente");
      }
      handleCloseServicio();
      fetchServicios();
    } catch (err) {
      alert("❌ Error: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteServicio = async (servicio) => {
    if (window.confirm(`¿Eliminar el servicio "${servicio.nombre}"?`)) {
      try {
        await servicioService.delete(servicio.id);
        fetchServicios();
      } catch (err) {
        alert(err.response?.data?.message || "Error al eliminar");
      }
    }
  };

  // Handlers Paquete
  const handleOpenPaquete = (paquete = null) => {
    if (paquete) {
      setIsEdit(true);
      setCurrentId(paquete.id);
      const data = {
        nombre: paquete.nombre || "",
        descripcion: paquete.descripcion || "",
        precio: paquete.precio || "",
      };
      setPaqueteForm({ ...data });
      setOriginalData({ ...data });
    } else {
      setIsEdit(false);
      setCurrentId(null);
      setPaqueteForm(initialPaquete);
      setOriginalData(null);
    }
    setOpenPaquete(true);
  };

  const handleClosePaquete = () => {
    setOpenPaquete(false);
    setPaqueteForm(initialPaquete);
    setOriginalData(null);
  };

  const handleSubmitPaquete = async (e) => {
    e.preventDefault();
    if (!paqueteForm.nombre.trim()) {
      alert("El nombre del paquete es requerido");
      return;
    }
    try {
      if (isEdit) {
        await paqueteService.update({ ...paqueteForm, id: currentId });
        alert("✓ Paquete actualizado correctamente");
      } else {
        await paqueteService.create(paqueteForm);
        alert("✓ Paquete creado exitosamente");
      }
      handleClosePaquete();
      fetchPaquetes();
    } catch (err) {
      alert("❌ Error: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDeletePaquete = async (paquete) => {
    if (window.confirm(`¿Eliminar el paquete "${paquete.nombre}"?`)) {
      try {
        await paqueteService.delete(paquete.id);
        fetchPaquetes();
      } catch (err) {
        alert(err.response?.data?.message || "Error al eliminar");
      }
    }
  };

  // Filter
  const filteredServicios = servicios.filter(
    (s) =>
      s.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      s.descripcion?.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredPaquetes = paquetes.filter(
    (p) =>
      p.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      p.descripcion?.toLowerCase().includes(search.toLowerCase()),
  );

  // Format price
  const formatPrice = (precio) => {
    const p = parseFloat(precio) || 0;
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(p);
  };

  // Stats
  const totalServicios = servicios.length;
  const totalPaquetes = paquetes.length;
  const sumaPaquetes = paquetes.reduce(
    (acc, p) => acc + (parseFloat(p.precio) || 0),
    0,
  );
  const promedioPaquetes = totalPaquetes > 0 ? sumaPaquetes / totalPaquetes : 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1a237e",
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Catálogo de Servicios
        </Typography>
        {/* Botón desktop */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() =>
              tabValue === 0 ? handleOpenServicio() : handleOpenPaquete()
            }
            sx={{
              background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
              boxShadow: "0 3px 5px 2px rgba(26, 35, 126, .3)",
              borderRadius: 2,
            }}
          >
            {tabValue === 0 ? "Nuevo Servicio" : "Nuevo Paquete"}
          </Button>
        </Box>
        {/* Botón móvil */}
        <IconButton
          onClick={() =>
            tabValue === 0 ? handleOpenServicio() : handleOpenPaquete()
          }
          sx={{
            display: { xs: "flex", sm: "none" },
            width: 44,
            height: 44,
            background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
            color: "#fff",
            boxShadow: "0 3px 8px rgba(26, 35, 126, .4)",
            "&:hover": {
              background: "linear-gradient(45deg, #283593 30%, #3949ab 90%)",
            },
          }}
        >
          <Add />
        </IconButton>
      </Box>

      {/* Stats Cards — círculos */}
      <Grid
        container
        spacing={{ xs: 1, sm: 3 }}
        justifyContent="center"
        sx={{ mb: 4 }}
      >
        {[
          {
            label: "Servicios",
            value: totalServicios,
            color: "#6B8EFF",
            glow: "rgba(107,142,255,0.35)",
            bg: "radial-gradient(circle at 35% 35%, #1e2d6b, #0d1540)",
            icon: <Category sx={{ fontSize: { xs: 22, sm: 28 } }} />,
          },
          {
            label: "Paquetes",
            value: totalPaquetes,
            color: "#C47AFF",
            glow: "rgba(196,122,255,0.35)",
            bg: "radial-gradient(circle at 35% 35%, #3b1466, #1a0838)",
            icon: <Inventory2 sx={{ fontSize: { xs: 22, sm: 28 } }} />,
          },
          {
            label: "Precio Promedio",
            value: formatPrice(promedioPaquetes),
            color: "#6BFF9E",
            glow: "rgba(107,255,158,0.3)",
            bg: "radial-gradient(circle at 35% 35%, #1a5e30, #0b2d17)",
            icon: <AttachMoney sx={{ fontSize: { xs: 22, sm: 28 } }} />,
          },
        ].map((stat, i) => (
          <Grid
            item
            xs={4}
            sm="auto"
            key={i}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Box
              sx={{
                width: { xs: 100, sm: 140 },
                height: { xs: 100, sm: 140 },
                borderRadius: "50%",
                background: stat.bg,
                border: `2px solid ${stat.color}50`,
                boxShadow: `0 0 24px ${stat.glow}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.25, sm: 0.5 },
              }}
            >
              <Box sx={{ color: stat.color, lineHeight: 1 }}>{stat.icon}</Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1rem", sm: "1.35rem" },
                  color: "#fff",
                  lineHeight: 1,
                  textAlign: "center",
                  px: 0.5,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.55rem", sm: "0.65rem" },
                  color: "rgba(255,255,255,0.65)",
                  textAlign: "center",
                  lineHeight: 1.2,
                  px: 1,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 3 }}>
        <Box
          sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#1a237e" }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, v) => {
              setTabValue(v);
              setPage(0);
              setSearch("");
            }}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: "#00C9FF" } }}
          >
            <Tab
              label="Servicios"
              icon={<Category />}
              iconPosition="start"
              sx={{ color: "white", fontWeight: 600 }}
            />
            <Tab
              label="Paquetes"
              icon={<LocalOffer />}
              iconPosition="start"
              sx={{ color: "white", fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* Search Bar */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder={`Buscar ${tabValue === 0 ? "servicios" : "paquetes"}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* TAB: SERVICIOS */}
        <TabPanel value={tabValue} index={0}>
          {loadingServicios ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : errorServicios ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {errorServicios}
            </Alert>
          ) : isMobile ? (
            /* Vista Móvil - Servicios */
            <Box>
              <MobileDataList
                loading={loadingServicios}
                emptyMessage="No hay servicios registrados"
              >
                {filteredServicios
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((servicio) => (
                    <MobileDataCard
                      key={servicio.id}
                      title={servicio.nombre || "Sin nombre"}
                      subtitle={`ID: ${servicio.id}`}
                      avatarColor="#1a237e"
                      primaryInfo={[
                        {
                          icon: <AttachMoney fontSize="small" />,
                          label: "Precio",
                          value: formatPrice(servicio.precio),
                        },
                        {
                          icon: <Description fontSize="small" />,
                          label: "Descripción",
                          value: servicio.descripcion?.substring(0, 30) || "-",
                        },
                      ]}
                      actions={[
                        {
                          icon: <Edit fontSize="small" />,
                          label: "Editar",
                          onClick: () => handleOpenServicio(servicio),
                        },
                        {
                          icon: <Delete fontSize="small" />,
                          label: "Eliminar",
                          color: "error",
                          onClick: () => handleDeleteServicio(servicio),
                        },
                      ]}
                    />
                  ))}
                </MobileDataList>
                {/* Paginación móvil */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredServicios.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="Por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} de ${count}`
                  }
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      flexWrap: "wrap",
                      justifyContent: "center",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      display: { xs: "none", sm: "block" },
                    },
                  }}
                />
              </Box>
          ) : (
            /* Vista Desktop - Servicios */
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#1a237e" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Servicio
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Descripción
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Precio
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", color: "#fff" }}
                      >
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredServicios
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((servicio) => (
                        <TableRow key={servicio.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {servicio.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "#1a237e20",
                                  color: "#1a237e",
                                }}
                              >
                                {getServiceIcon(servicio.nombre)}
                              </Avatar>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {servicio.nombre}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 300 }}
                              noWrap
                              title={servicio.descripcion}
                            >
                              {servicio.descripcion || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="success.main"
                            >
                              {formatPrice(servicio.precio)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenServicio(servicio)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteServicio(servicio)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredServicios.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">
                            No se encontraron servicios
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredServicios.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
              />
            </>
          )}
        </TabPanel>

        {/* TAB: PAQUETES */}
        <TabPanel value={tabValue} index={1}>
          {loadingPaquetes ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : errorPaquetes ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {errorPaquetes}
            </Alert>
          ) : isMobile ? (
            /* Vista Móvil - Paquetes */
            <Box>
              <MobileDataList
                loading={loadingPaquetes}
                emptyMessage="No hay paquetes registrados"
              >
                {filteredPaquetes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((paquete) => {
                    const categoria = getPaqueteColor(paquete.precio);
                    return (
                      <MobileDataCard
                        key={paquete.id}
                        title={paquete.nombre || "Sin nombre"}
                        subtitle={`ID: ${paquete.id}`}
                        avatarColor="#7b1fa2"
                        status={{
                          label: categoria.label,
                          color: categoria.color,
                        }}
                        primaryInfo={[
                          {
                            icon: <AttachMoney fontSize="small" />,
                            label: "Precio",
                            value: formatPrice(paquete.precio),
                          },
                          {
                            icon: <Description fontSize="small" />,
                            label: "Descripción",
                            value: paquete.descripcion?.substring(0, 30) || "-",
                          },
                        ]}
                        actions={[
                          {
                            icon: <Edit fontSize="small" />,
                            label: "Editar",
                            onClick: () => handleOpenPaquete(paquete),
                          },
                          {
                            icon: <Delete fontSize="small" />,
                            label: "Eliminar",
                            color: "error",
                            onClick: () => handleDeletePaquete(paquete),
                          },
                        ]}
                      />
                    );
                  })}
              </MobileDataList>
              <TablePagination
                component="div"
                count={filteredPaquetes.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
              />
            </Box>
          ) : (
            /* Vista Desktop - Paquetes */
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#1a237e" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Paquete
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Descripción
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Precio
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                        Categoría
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", color: "#fff" }}
                      >
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPaquetes
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((paquete) => {
                        const categoria = getPaqueteColor(paquete.precio);
                        return (
                          <TableRow key={paquete.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {paquete.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "#7b1fa220",
                                    color: "#7b1fa2",
                                  }}
                                >
                                  <LocalOffer />
                                </Avatar>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {paquete.nombre}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ maxWidth: 300 }}
                                noWrap
                                title={paquete.descripcion}
                              >
                                {paquete.descripcion || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                color="success.main"
                              >
                                {formatPrice(paquete.precio)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={categoria.label}
                                size="small"
                                sx={{
                                  bgcolor: categoria.bg,
                                  color: categoria.color,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenPaquete(paquete)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeletePaquete(paquete)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredPaquetes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">
                            No se encontraron paquetes
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredPaquetes.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
              />
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Dialog Servicio */}
      <Dialog
        open={openServicio}
        onClose={handleCloseServicio}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
            color: "white",
          }}
        >
          {isEdit ? "Editar Servicio" : "Nuevo Servicio"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <form id="servicio-form" onSubmit={handleSubmitServicio}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del Servicio"
                  required
                  value={servicioForm.nombre}
                  onChange={(e) =>
                    setServicioForm({ ...servicioForm, nombre: e.target.value })
                  }
                  placeholder="Ej: Fotografía, Video, Drone..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Category color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  multiline
                  rows={3}
                  value={servicioForm.descripcion}
                  onChange={(e) =>
                    setServicioForm({
                      ...servicioForm,
                      descripcion: e.target.value,
                    })
                  }
                  placeholder="Descripción detallada del servicio..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Precio (MXN)"
                  type="number"
                  value={servicioForm.precio}
                  onChange={(e) =>
                    setServicioForm({ ...servicioForm, precio: e.target.value })
                  }
                  placeholder="Ej: 5000"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseServicio}>Cancelar</Button>
          <Button
            variant="contained"
            type="submit"
            form="servicio-form"
            disabled={!servicioForm.nombre || (isEdit && !hasServicioChanges)}
          >
            {isEdit ? "Guardar Cambios" : "Crear Servicio"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Paquete */}
      <Dialog
        open={openPaquete}
        onClose={handleClosePaquete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)",
            color: "white",
          }}
        >
          {isEdit ? "Editar Paquete" : "Nuevo Paquete"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <form id="paquete-form" onSubmit={handleSubmitPaquete}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del Paquete"
                  required
                  value={paqueteForm.nombre}
                  onChange={(e) =>
                    setPaqueteForm({ ...paqueteForm, nombre: e.target.value })
                  }
                  placeholder="Ej: Paquete Básico, Premium, Platino..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOffer color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Precio (MXN)"
                  type="number"
                  value={paqueteForm.precio}
                  onChange={(e) =>
                    setPaqueteForm({ ...paqueteForm, precio: e.target.value })
                  }
                  placeholder="Ej: 15000"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  multiline
                  rows={3}
                  value={paqueteForm.descripcion}
                  onChange={(e) =>
                    setPaqueteForm({
                      ...paqueteForm,
                      descripcion: e.target.value,
                    })
                  }
                  placeholder="Qué incluye este paquete..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePaquete}>Cancelar</Button>
          <Button
            variant="contained"
            type="submit"
            form="paquete-form"
            color="secondary"
            disabled={!paqueteForm.nombre || (isEdit && !hasPaqueteChanges)}
          >
            {isEdit ? "Guardar Cambios" : "Crear Paquete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminServicios;
